import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const BDCityMetricsDashboard = ({ report }) => {
  if (!report) return null;

  const cityData = report?.cityMetrics || [];
  const supplyDemand = report?.supplyDemand || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      {/* YoY Growth Chart */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="BarChart2" size={16} className="text-green-600" />
          <h3 className="font-semibold text-gray-900 text-sm">YoY Price Growth by City (%)</h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={cityData} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip
              formatter={(value) => [`${value}%`, 'YoY Growth']}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
            <Bar dataKey="yoyGrowth" fill="#16a34a" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Supply vs Demand */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Activity" size={16} className="text-purple-600" />
          <h3 className="font-semibold text-gray-900 text-sm">Supply vs Demand Index</h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={supplyDemand} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="supply" stroke="#8b5cf6" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="demand" stroke="#f59e0b" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Investment Hotspots */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="MapPin" size={16} className="text-red-500" />
          <h3 className="font-semibold text-gray-900 text-sm">Investment Hotspot Rankings</h3>
        </div>
        <div className="space-y-2">
          {(report?.investmentHotspots || [])?.map((spot, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
              <div className="flex-1">
                <div className="flex justify-between items-center mb-0.5">
                  <span className="text-sm font-medium text-gray-800">{spot?.area}</span>
                  <span className="text-xs text-green-600 font-semibold">{spot?.roi}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${spot?.score}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Median Price Heatmap */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Grid" size={16} className="text-emerald-600" />
          <h3 className="font-semibold text-gray-900 text-sm">Median Price by City (৳/sqft)</h3>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {cityData?.map((c, i) => {
            const maxPrice = Math.max(...cityData?.map(d => d?.medianPriceSqft || 0));
            const intensity = maxPrice > 0 ? (c?.medianPriceSqft || 0) / maxPrice : 0;
            const bg = intensity > 0.8 ? 'bg-green-700' : intensity > 0.6 ? 'bg-green-500' : intensity > 0.4 ? 'bg-green-400' : intensity > 0.2 ? 'bg-green-300' : 'bg-green-100';
            const text = intensity > 0.5 ? 'text-white' : 'text-green-900';
            return (
              <div key={i} className={`${bg} rounded-lg p-3 flex flex-col gap-0.5`}>
                <span className={`text-xs font-medium ${text} opacity-80`}>{c?.name}</span>
                <span className={`text-sm font-bold ${text}`}>৳{(c?.medianPriceSqft || 0)?.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BDCityMetricsDashboard;
